// Call-out response performance (/office/sla-report). The office-readable view of how well ELE kept
// the response-time promises it made: response within target, fix within target, first-time fix,
// broken down by customer, site and coverage zone. Office AND admin (the zone CONFIG at
// /office/coverage-zones is admin only: the office reads performance, it cannot change the promise).
//
// ---------------------------------------------------------------------------
// THIS PAGE IS RUN FROM IN A RENEWAL CONVERSATION, SO IT IS HONEST BEFORE IT IS FLATTERING
// ---------------------------------------------------------------------------
// Every figure comes from GET /api/sla/report, computed by the pure server aggregate
// (server/src/modules/sla/slaStats.ts) from outcomes the SLA clock already judged and persisted.
// NOTHING is recomputed here: the page renders the counts and percentages it is given, and the only
// arithmetic it does is adding two counts the aggregate already returned (met + breached) to state
// the measured base out loud.
//
// The rules that shape every render below, carried from the aggregate's honesty rule:
//   1. A NULL percentage renders as "No data". NEVER 0%, never 100%. Null means the denominator was
//      empty, i.e. we could not measure, not that we failed and not that we aced it. It is also
//      never given a positive colour: a figure resting on nothing must not read green.
//   2. The measured base is stated next to every headline ("Measured on 12 of 20 call-outs. 8
//      unmeasured."), so a number resting on thin data is visibly thin at a glance.
//   3. "Not targeted" is neither a pass nor a failure, and is never coloured as either. ELE
//      publishes no fix-time promise, so a fix leg with no target is the NORMAL state, not a fault.
//   4. The "No zone set" row is a GAP for the office to close (a site with no postcode), and says so
//      rather than hiding.
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { api } from "../../lib/api";
import type { SlaFirstTimeFixStats, SlaGroupStats, SlaOutcomeCounts, SlaReport, SlaStats } from "../../lib/api";
import { formatSlaMinutes, slaApiErrorText } from "../../lib/slaFormat";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { Loader } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";
import { StatusPill, toneTextClass, type PillTone } from "../../components/StatusPill";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";

// The server defaults to the last 90 days when both dates are blank. The date inputs are PRE-FILLED
// with that same window (rather than left empty) so the office can see the period the figures
// describe without having to know the default. Computed in whole UTC days to match the server's
// parseSlaWindow exactly, so the pre-filled dates are the ones the server would have chosen anyway.
const DEFAULT_WINDOW_DAYS = 90;
const DAY_MS = 24 * 60 * 60 * 1000;

export function defaultSlaWindow(now: Date): { from: string; to: string } {
  const end = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const start = end - (DEFAULT_WINDOW_DAYS - 1) * DAY_MS;
  const fmt = (ms: number) => new Date(ms).toISOString().slice(0, 10);
  return { from: fmt(start), to: fmt(end) };
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

// ---- The honesty rule, rendered ---------------------------------------------
// THE most important function on this page. A null percentage is "No data", full stop: not 0%, not
// 100%, not a dash that reads like a zero. Pure.
export const NO_DATA = "No data";

export function formatPct(pct: number | null): string {
  return pct == null ? NO_DATA : `${pct}%`;
}

// The colour a percentage earns. A null percentage is NEUTRAL: colouring "No data" green would let a
// month with no evidence read as a good month, which is the whole thing this page refuses to do.
// The bands mirror the PPM compliance panel's (90 / 60), the platform's existing reporting idiom.
export type PctTone = "positive" | "caution" | "alert" | "neutral";

export function pctTone(pct: number | null): PctTone {
  if (pct == null) return "neutral";
  if (pct >= 90) return "positive";
  if (pct >= 60) return "caution";
  return "alert";
}

// The text colour comes from the shared tone helper (the spec's `slaPercent`: not a status, so it is
// a function over a PillTone and never a registry entry).
const PCT_PALETTE: Record<PctTone, PillTone> = {
  positive: "green",
  caution: "amber",
  alert: "red",
  neutral: "grey",
};
function pctTextClass(pct: number | null): string {
  return toneTextClass(PCT_PALETTE[pctTone(pct)]);
}

// How many call-outs a within-target percentage actually rests on. This is the ONE piece of
// arithmetic on the page, and it is the aggregate's own denominator restated: met + breached are the
// judged legs, everything else is outside the percentage by design.
export function measuredCount(counts: SlaOutcomeCounts): number {
  return counts.met + counts.breached;
}

// The measured base for the RESPONSE headline, in plain English. Every call-out outside the
// percentage is named and counted, so "12 of 20" is never left as an unexplained gap. Pure.
export function responseBase(stats: SlaStats): string {
  if (stats.total === 0) return "No call-outs were reported in this period.";
  const measured = measuredCount(stats.responseBreakdown);
  const head = `Measured on ${measured} of ${stats.total} ${plural(stats.total, "call-out", "call-outs")}.`;
  const excluded: string[] = [];
  if (stats.responseBreakdown.unmeasured > 0) excluded.push(`${stats.responseBreakdown.unmeasured} unmeasured`);
  if (stats.responseBreakdown.open > 0) excluded.push(`${stats.responseBreakdown.open} still open`);
  if (stats.responseBreakdown.not_targeted > 0) excluded.push(`${stats.responseBreakdown.not_targeted} with no target set`);
  return excluded.length === 0 ? head : `${head} ${excluded.join(", ")}.`;
}

// The measured base for the FIX headline. "Not targeted" is named as what it is (neither a pass nor
// a failure), because on the fix leg it is the normal state rather than a data gap. Pure.
export function fixBase(stats: SlaStats): string {
  if (stats.total === 0) return "No call-outs were reported in this period.";
  const measured = measuredCount(stats.fixBreakdown);
  const head = `Measured on ${measured} of ${stats.total} ${plural(stats.total, "call-out", "call-outs")}.`;
  const excluded: string[] = [];
  if (stats.fixBreakdown.not_targeted > 0) {
    excluded.push(`${stats.fixBreakdown.not_targeted} with no fix target set, which is neither a pass nor a failure`);
  }
  if (stats.fixBreakdown.unmeasured > 0) excluded.push(`${stats.fixBreakdown.unmeasured} unmeasured`);
  if (stats.fixBreakdown.open > 0) excluded.push(`${stats.fixBreakdown.open} still open`);
  return excluded.length === 0 ? head : `${head} ${excluded.join(", ")}.`;
}

// The assessed base for the FIRST-TIME FIX headline. Its population is completed call-outs, not all
// call-outs, so it says "completed" rather than borrowing the window total. Pure.
export function firstTimeFixBase(ftf: SlaFirstTimeFixStats): string {
  const assessed = ftf.fixed + ftf.notFixed;
  const completed = assessed + ftf.unassessable;
  if (completed === 0) return "No call-outs were completed in this period.";
  const head = `Assessed on ${assessed} of ${completed} completed ${plural(completed, "call-out", "call-outs")}.`;
  return ftf.unassessable > 0 ? `${head} ${ftf.unassessable} could not be assessed.` : head;
}

// ---- Small building blocks --------------------------------------------------
function Panel({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <section className="rounded-ele border border-ele-purple-accent bg-ele-surface-soft p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-xl text-ele-blue">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

// A headline figure with its measured base underneath. The base is NOT optional and NOT a tooltip:
// an office manager must see what the number rests on without hovering or asking.
function Headline({ label, pct, base, hint }: { label: string; pct: number | null; base: string; hint: string }) {
  return (
    <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
      <div className="font-menu text-xs uppercase tracking-widest text-ele-blue">{label}</div>
      <div className={`mt-2 font-heading text-4xl ${pctTextClass(pct)}`}>{formatPct(pct)}</div>
      <p className="mt-2 text-sm text-ele-body">{base}</p>
      <p className="mt-1 text-xs text-ele-body opacity-70">{hint}</p>
    </div>
  );
}

// The five outcomes as pills. met is green and breached is red; open, unmeasured and NOT TARGETED
// are all grey, because none of them is a pass or a failure and none may be coloured as one.
// (The words and tones are the registry's `slaOutcome` domain.)
const OUTCOME_ORDER: (keyof SlaOutcomeCounts)[] = ["met", "breached", "open", "not_targeted", "unmeasured"];

function OutcomeBreakdown({ title, counts }: { title: string; counts: SlaOutcomeCounts }) {
  return (
    <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
      <h3 className="mb-3 font-heading text-sm uppercase tracking-wide text-ele-body">{title}</h3>
      <ul className="space-y-2">
        {OUTCOME_ORDER.map((key) => (
          <li key={key} className="flex items-center justify-between gap-3">
            <StatusPill domain="slaOutcome" status={key} />
            <span className="font-heading text-ele-strong">{counts[key]}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---- The breakdown tables ---------------------------------------------------
// Columns are fixed-width percentages over the shared .ele-list-table (table-layout: fixed), so the
// table fits its container and truncates rather than overflowing at any width. Below md the three
// supporting columns fold away, leaving who / how many / how we did.
//
// Mobile truncation verification pass: this GroupTable is shared by "By area" (heading is one of
// three short zone names, LOCAL/GREATER/OUTER -- genuinely fine at any width) AND "By customer" /
// "By site", whose heading is a REAL customer or site name, unbounded length. The colgroup keeps
// its literal desktop share when a sibling column hides (the AHB finding this whole session is
// verifying), so at 375px the 34%-wide heading column measured well under what a real name needs,
// wrapped in overflow-hidden (clipped, no scroll fallback). The empty-state fallback text is
// "No site linked" / "No customer on record" -- close to a verbatim match for the confirmed bug
// report's own "No site li..." truncation example. Card list below md, table otherwise, decided
// once at mount (src/lib/mobileLayout.ts); jsdom has no matchMedia, so the existing table-scoped
// tests are unaffected.
function GroupTable({
  title,
  heading,
  rows,
  note,
  emptyText,
}: {
  title: string;
  heading: string;
  rows: SlaGroupStats[];
  note?: ReactNode;
  emptyText: string;
}) {
  const [mobileCards] = useState(initialMobileLayout);
  return (
    <Panel title={title}>
      {rows.length === 0 ? (
        <EmptyState compact message={emptyText} />
      ) : (
        <>
          {mobileCards ? (
            <MobileCardList label={heading}>
              {rows.map((r) => {
                return (
                  <MobileListCard
                    key={r.key ?? "__none__"}
                    primary={r.label}
                    secondary={`${r.total} call-out${r.total === 1 ? "" : "s"} · measured ${measuredCount(r.responseBreakdown)} of ${r.total}`}
                    meta={`Average response ${formatSlaMinutes(r.avgResponseMins)}`}
                    right={
                      <>
                        <span className={`font-heading text-base ${pctTextClass(r.responseWithinTargetPct)}`}>{formatPct(r.responseWithinTargetPct)}</span>
                        <span className="text-[10px] uppercase tracking-wide text-ele-body opacity-70">Within target</span>
                      </>
                    }
                  />
                );
              })}
            </MobileCardList>
          ) : (
            <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
              <table className="ele-list-table w-full text-left text-sm">
                <colgroup>
                  <col style={{ width: "34%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "18%" }} />
                  <col className="max-md:hidden" style={{ width: "16%" }} />
                  <col className="max-md:hidden" style={{ width: "18%" }} />
                </colgroup>
                <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
                  <tr>
                    <th className="px-3 py-2">{heading}</th>
                    <th className="px-3 py-2 text-right">Call-outs</th>
                    <th className="px-3 py-2 text-right">Within target</th>
                    <th className="px-3 py-2 text-right max-md:hidden">Measured</th>
                    <th className="px-3 py-2 text-right max-md:hidden">Average response</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.key ?? "__none__"}>
                      <td className="truncate px-3 py-2 text-ele-strong" title={r.label}>
                        {r.label}
                      </td>
                      <td className="px-3 py-2 text-right text-ele-body">{r.total}</td>
                      <td className={`px-3 py-2 text-right ${pctTextClass(r.responseWithinTargetPct)}`}>
                        {formatPct(r.responseWithinTargetPct)}
                      </td>
                      <td className="px-3 py-2 text-right text-ele-body max-md:hidden">
                        {measuredCount(r.responseBreakdown)} of {r.total}
                      </td>
                      <td className="px-3 py-2 text-right text-ele-body max-md:hidden">
                        {formatSlaMinutes(r.avgResponseMins)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {note ? <p className="mt-3 text-xs text-ele-body opacity-70">{note}</p> : null}
        </>
      )}
    </Panel>
  );
}

// ---- The page ---------------------------------------------------------------
export function SlaReportPage() {
  const initial = defaultSlaWindow(new Date());
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [applied, setApplied] = useState(initial);
  const [data, setData] = useState<SlaReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setError(null);
    setData(null);
    api.sla
      .report({ from: applied.from || undefined, to: applied.to || undefined })
      .then((d) => {
        if (active) setData(d);
      })
      .catch((err: unknown) => {
        // The server owns the wording of a bad date range, so it is shown verbatim rather than
        // second-guessed here.
        if (active) setError(slaApiErrorText(err, "The response performance report could not be loaded. Please try again."));
      });
    return () => {
      active = false;
    };
  }, [applied, reloadKey]);

  const applyWindow = useCallback(() => setApplied({ from, to }), [from, to]);
  const retry = useCallback(() => setReloadKey((k) => k + 1), []);

  return (
    <div>
      <PageHeader
        className="mb-5"
        title="Call-out response performance"
        subtitle={
          <>
            How quickly ELE got an engineer assigned to the call-outs reported in this period, against the response times
            promised for each area, and how long the engineer then spent on site clearing the fault. Every figure counts only
            the call-outs we can actually measure, and says so.
          </>
        }
        actions={
          <>
            <label className="block">
              <span className="mb-1 block font-menu text-[10px] uppercase tracking-wide text-ele-blue">From</span>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-sm text-ele-strong"
              />
            </label>
            <label className="block">
              <span className="mb-1 block font-menu text-[10px] uppercase tracking-wide text-ele-blue">To</span>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-sm text-ele-strong"
              />
            </label>
            <Button onClick={applyWindow}>Apply</Button>
          </>
        }
      />

      {error ? (
        <div className="rounded-ele border border-ele-purple-accent bg-ele-surface-soft p-5">
          <p role="alert" className="text-sm text-ele-danger">
            {error}
          </p>
          <button
            onClick={retry}
            className="mt-3 rounded-ele border border-ele-purple-accent px-4 py-1.5 font-heading text-sm tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-strong"
          >
            Try again
          </button>
        </div>
      ) : data === null ? (
        <Loader />
      ) : (
        <div className="space-y-6">
          <Panel
            title="Headline figures"
            action={
              <span className="font-menu text-xs uppercase tracking-wide text-ele-body opacity-70">{data.window.label}</span>
            }
          >
            <div className="grid gap-4 lg:grid-cols-3">
              <Headline
                label="Engineer assigned within target"
                pct={data.overall.responseWithinTargetPct}
                base={responseBase(data.overall)}
                hint="Of the call-outs we could measure, how many had an engineer assigned inside the promised response time."
              />
              <Headline
                label="Fixed within target"
                pct={data.overall.fixWithinTargetPct}
                base={fixBase(data.overall)}
                hint="Of the call-outs with a fix-time target, how many the engineer finished inside it, counted from arriving on site."
              />
              <Headline
                label="Fixed first time"
                pct={data.firstTimeFix.firstTimeFixPct}
                base={firstTimeFixBase(data.firstTimeFix)}
                hint={`Completed with no follow-up needed and no repeat call-out on the same lift within ${data.recurrenceDays} days.`}
              />
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
                <div className="font-menu text-xs uppercase tracking-widest text-ele-blue">Average response</div>
                <div className="mt-2 font-heading text-2xl text-ele-strong">{formatSlaMinutes(data.overall.avgResponseMins)}</div>
                <div className="mt-1 text-xs text-ele-body opacity-70">From logging to an engineer assigned, measured call-outs only</div>
              </div>
              <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
                <div className="font-menu text-xs uppercase tracking-widest text-ele-blue">Typical response</div>
                <div className="mt-2 font-heading text-2xl text-ele-strong">{formatSlaMinutes(data.overall.medianResponseMins)}</div>
                <div className="mt-1 text-xs text-ele-body opacity-70">The middle call-out: half were quicker, half slower</div>
              </div>
              <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
                <div className="font-menu text-xs uppercase tracking-widest text-ele-blue">Average time on site</div>
                <div className="mt-2 font-heading text-2xl text-ele-strong">{formatSlaMinutes(data.overall.avgFixMins)}</div>
                <div className="mt-1 text-xs text-ele-body opacity-70">
                  From arriving to signing off. Typical: {formatSlaMinutes(data.overall.medianFixMins)}
                </div>
              </div>
              <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
                <div className="font-menu text-xs uppercase tracking-widest text-ele-blue">Call-outs reported</div>
                <div className="mt-2 font-heading text-2xl text-ele-strong">{data.overall.total}</div>
                <div className="mt-1 text-xs text-ele-body opacity-70">Everything raised in this period</div>
              </div>
              <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
                <div className="font-menu text-xs uppercase tracking-widest text-ele-blue">Could not be measured</div>
                <div className="mt-2 font-heading text-2xl text-ele-strong">{formatPct(data.overall.unmeasuredPct)}</div>
                <div className="mt-1 text-xs text-ele-body opacity-70">
                  {data.overall.responseBreakdown.unmeasured} of {data.overall.total} had no usable times
                </div>
              </div>
              <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
                <div className="font-menu text-xs uppercase tracking-widest text-ele-blue">On SLA hold</div>
                <div className="mt-2 font-heading text-2xl text-ele-strong">{data.overall.heldCallouts ?? 0}</div>
                <div className="mt-1 text-xs text-ele-body opacity-70">
                  {(data.overall.heldMins ?? 0) > 0
                    ? `Call-outs that waited on the customer, ${formatSlaMinutes(data.overall.heldMins ?? 0)} in all. Left out of the response times, still counted above.`
                    : "No call-out waited on the customer in this period"}
                </div>
              </div>
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <OutcomeBreakdown title="Getting an engineer assigned (response)" counts={data.overall.responseBreakdown} />
              <OutcomeBreakdown title="Clearing the fault on site (fix)" counts={data.overall.fixBreakdown} />
            </div>
          </Panel>

          <Panel title="How to read this page">
            <div className="space-y-3 text-sm text-ele-body">
              <p>
                <span className="text-ele-strong">The response clock</span> runs from the moment a call-out is reported to us
                until an engineer is assigned to it. Each area has a target time we aim to beat, so "within target" means an
                engineer was on the job before the clock ran out. The engineer's travel to site is not part of it.
              </p>
              <p>
                <span className="text-ele-strong">An SLA hold</span> pauses the response clock while we wait on the customer, for
                example for payment before attending. The time on hold is left out of the response time and shown on its own; the
                call-out is still counted.
              </p>
              <p>
                <span className="text-ele-strong">The fix clock</span> starts when the engineer arrives on site and stops when
                the lift is signed off as working or made safe. It never includes the time before the engineer arrived.
              </p>
              <p>
                <span className="text-ele-strong">Some call-outs cannot be measured</span>, and those are left out of the
                percentages rather than guessed at. That happens when a call-out was raised before we started recording these
                times (the time an engineer was assigned has only been recorded since 29 September 2026), or when it was
                logged with a date but no time of day, so there is no honest starting point to count from. A call-out we
                cannot measure is never counted as a success, and never as a failure.
              </p>
              <p>
                <span className="text-ele-strong">"Not targeted"</span> means nobody set a target for that part of the job. We
                promise a response time, not a repair time, so most call-outs have no fix target. Those are neither a pass nor
                a failure and are kept out of the fix percentage entirely.
              </p>
              <p>
                <span className="text-ele-strong">"No data"</span> means there was nothing we could measure in this period. It
                does not mean nothing went wrong, and it does not mean everything went perfectly.
              </p>
            </div>
          </Panel>

          <GroupTable
            title="By customer"
            heading="Customer"
            rows={data.byCustomer}
            emptyText="No call-outs were reported in this period."
            note="Busiest first. A call-out logged against a caller with no customer record on file appears as 'No customer on record'."
          />
          <GroupTable
            title="By site"
            heading="Site"
            rows={data.bySite}
            emptyText="No call-outs were reported in this period."
            note="Busiest first. A call-out raised without a lift on record has no site to group under and appears as 'No site linked'."
          />
          <GroupTable
            title="By area"
            heading="Coverage zone"
            rows={data.byZone}
            emptyText="No call-outs were reported in this period."
            note="Busiest first. A 'No zone set' row means those call-outs were at sites we could not place in a coverage area, usually because the site has no postcode on file: worth adding, so the right response target applies next time."
          />
        </div>
      )}
    </div>
  );
}
